import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { min, max } from 'd3'
import { FormattedMessage } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import { ARM_MORTGAGE_TYPE } from 'src/constants/loan'
import { REFI_CHART_MESSAGES } from 'src/constants/refinance'
import {
  selectCustomerIsREA,
  selectCustomerHasSponsor,
  selectCustomerFirstName,
  selectHasMortgageCoach
} from 'src/store/selectors/customerProfile'
import { Text, If, Unless, Format } from 'src/Components'
import RefiViz from 'src/Components/shared/RefiViz'
import RefiMessages from 'src/Components/shared/InterestSection/RefiByYearsInHome/RefiMessages'
import MortgageCoachRequest from 'src/Components/shared/InterestSection/MortgageCoach/MortgageCoachRequest'
import RefiRequestButton from './RequestButton'

import styles from './RefiDetailsOverlay.module.scss'

const propTypes = {
  type: PropTypes.string.isRequired,
  term: PropTypes.number.isRequired,
  years: PropTypes.number.isRequired,
  rate: PropTypes.number.isRequired,
  apr: PropTypes.number.isRequired,
  longterm: PropTypes.number.isRequired,
  shortterm: PropTypes.number.isRequired,
  closing: PropTypes.number.isRequired,
  yearsInHome: PropTypes.number.isRequired,
  pmi: PropTypes.number,
  breakeven: PropTypes.number,
  potential30Year: PropTypes.number
}

const RefiDetailsBody = ({ pmi, yearsInHome, ...details }) => {
  const { type, years, datapoints } = details

  const intlFormat = useFormatMessage()

  const isREA = useSelector(selectCustomerIsREA)
  const hasSponsor = useSelector(selectCustomerHasSponsor)
  const hasMortgageCoach = useSelector(selectHasMortgageCoach)
  const contactFirstName = useSelector(selectCustomerFirstName)

  const chartOptions = useMemo(
    () =>
      datapoints
        ? {
            verticalPaddingFactor: 0,
            min: min(datapoints ? datapoints.map(d => d.y) : 0),
            max: max(datapoints ? datapoints.map(d => d.y) : 0),
            datapoints: datapoints.map(attrs => ({
              ...attrs,
              tooltipEmphasis:
                type === ARM_MORTGAGE_TYPE && attrs.x > years
                  ? intlFormat(REFI_CHART_MESSAGES.unknown)
                  : Format.currency(attrs.y),
              tooltipDescription: intlFormat(REFI_CHART_MESSAGES.tooltip, {
                year: attrs.x
              })
            }))
          }
        : null,
    [datapoints, type, years, intlFormat]
  )

  return (
    <div className={styles.body}>
      <div className={styles.headline}>
        <Text size='xl' weight='light'>
          <RefiMessages.DetailsHeadline {...details} />
        </Text>
      </div>
      <Unless condition={hasSponsor}>
        <If condition={hasMortgageCoach}>
          <MortgageCoachRequest
            className={styles.buttonInBody}
            {...details}
            contactName={contactFirstName}
            rootMatchPath={OVERLAY_HELPERS.refinance.matchPath}
          />
        </If>
        <Unless condition={hasMortgageCoach}>
          <RefiRequestButton className={styles.buttonInBody} {...details} />
        </Unless>
      </Unless>
      <div className={styles.specifics}>
        <div className={styles.vizualization}>
          <RefiViz {...details} options={chartOptions} />
        </div>
        <div className={styles.bullets}>
          <RefiMessages.DetailsBullets {...details} />
        </div>
      </div>
      <div className={styles.generic}>
        <Text size='l' weight='light' element='div'>
          <FormattedMessage
            id='Refinance.details.generic.title'
            defaultMessage='How much does refinancing cost?'
          />
        </Text>
        <If condition={isREA}>
          <Text color='quiet' size='s'>
            <FormattedMessage
              id='Refinance.details.generic.p2'
              defaultMessage='Mortgage estimates depend on market rates, your current financial situation, home occupancy status, and refi goals. Refinancing comes with costs, but you can sometimes roll those refi costs into your loan instead of bringing cash to the closing. Rates will be based on national trends and a good credit score.'
            />
          </Text>
        </If>
        <Unless condition={isREA}>
          <Text color='quiet' size='s'>
            <FormattedMessage
              id='Refinance.details.generic.p1'
              defaultMessage='{contactFirstName} is happy to walk you through their specific fee structure. Your mortgage estimate depends on market rates, your current financial situation, home occupancy status, and refi goals. Refinancing comes with costs, but you can sometimes roll those refi costs into your loan instead of bringing cash to the closing. Rates will be based on national trends and a good credit score.'
              values={{ contactFirstName }}
            />
          </Text>
        </Unless>
      </div>
    </div>
  )
}

RefiDetailsBody.propTypes = propTypes

export default RefiDetailsBody
